import { useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { Page } from '../../app/AppShell.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../../components/ui.js';
import { StatTile, RateBar } from '../../components/charts.js';
import { absoluteTime, money } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.js';
import type { BillingPlan, BillingStatus } from '../../lib/types.js';

const STATUS_LABEL: Record<BillingStatus, string> = {
  none: 'No subscription',
  trialing: 'Trialing',
  active: 'Active',
  past_due: 'Past due',
  canceled: 'Cancelled',
  incomplete: 'Incomplete',
  incomplete_expired: 'Incomplete (expired)',
};

const STATUS_TONE: Record<BillingStatus, 'neutral' | 'good' | 'warn' | 'bad'> = {
  none: 'neutral',
  trialing: 'good',
  active: 'good',
  past_due: 'bad',
  canceled: 'neutral',
  incomplete: 'warn',
  incomplete_expired: 'bad',
};

const SUBSCRIBED_STATUSES: BillingStatus[] = ['trialing', 'active', 'past_due'];

export function BillingScreen(): ReactNode {
  const overview = useQuery(() => api.billingOverview(), []);
  const { can } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const manage = can('billing.manage');

  async function openPortal(): Promise<void> {
    setBusy(true);
    try {
      const { url } = await api.createBillingPortalSession();
      window.location.href = url;
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <Page title="Billing">
      {overview.loading && !overview.data ? <Loading what="billing" /> : null}
      {overview.error ? <ErrorNote error={overview.error} /> : null}

      {overview.data ? (
        SUBSCRIBED_STATUSES.includes(overview.data.summary.status) ? (
          <div className="stack">
            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <StatTile
                label="Plan"
                value={overview.data.summary.planLabel ?? '—'}
                hint={overview.data.summary.interval ? `billed ${overview.data.summary.interval}ly` : undefined}
              />
              <StatTile
                label="Status"
                value={STATUS_LABEL[overview.data.summary.status]}
                tone={STATUS_TONE[overview.data.summary.status]}
                hint={
                  overview.data.summary.cancelAtPeriodEnd
                    ? `Cancels ${absoluteTime(overview.data.summary.currentPeriodEnd)}`
                    : overview.data.summary.currentPeriodEnd
                      ? `Renews ${absoluteTime(overview.data.summary.currentPeriodEnd)}`
                      : undefined
                }
              />
              <div className="card card-pad stat-tile" style={{ minWidth: 200 }}>
                <div className="stat-label">Displays</div>
                <div className="stat-value">
                  {overview.data.summary.seatsUsed}
                  {overview.data.summary.seatsLimit !== null ? ` / ${overview.data.summary.seatsLimit}` : ' (unlimited)'}
                </div>
                {overview.data.summary.seatsLimit !== null ? (
                  <RateBar value={overview.data.summary.seatsUsed / Math.max(1, overview.data.summary.seatsLimit)} />
                ) : null}
              </div>
            </div>

            {manage ? (
              <div>
                <button className="btn primary" disabled={busy} onClick={() => void openPortal()}>
                  {busy ? 'Opening…' : 'Manage billing'}
                </button>
                <p className="muted small" style={{ marginTop: 6 }}>
                  Change plan, update your payment method, or cancel — all handled by Stripe's secure portal.
                </p>
              </div>
            ) : null}

            <div>
              <h2 style={{ fontSize: 15, marginBottom: 8 }}>Billing history</h2>
              {overview.data.history.data.length === 0 ? (
                <EmptyState>No billing history yet.</EmptyState>
              ) : (
                <div className="card">
                  <table className="grid">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Event</th>
                        <th>Amount</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {overview.data.history.data.map((h) => (
                        <tr key={h.id}>
                          <td className="small" title={absoluteTime(h.occurredAt)}>
                            {absoluteTime(h.occurredAt)}
                          </td>
                          <td className="small">{h.summary}</td>
                          <td className="small">{money(h.amountCents, h.currency)}</td>
                          <td>
                            {h.hostedInvoiceUrl ? (
                              <a className="small" href={h.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                                View invoice
                              </a>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : (
          <PlanPicker manage={manage} />
        )
      ) : null}
    </Page>
  );
}

function PlanPicker({ manage }: { manage: boolean }): ReactNode {
  const plans = useQuery(() => api.billingPlans(), []);
  const toast = useToast();
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [subscribing, setSubscribing] = useState<string | null>(null);

  async function subscribe(plan: BillingPlan): Promise<void> {
    setSubscribing(plan.slug);
    try {
      const { url } = await api.createBillingCheckoutSession(plan.slug, interval);
      window.location.href = url;
    } catch (err) {
      toast.error(err);
      setSubscribing(null);
    }
  }

  return (
    <div className="stack">
      <p className="muted small">No active subscription. Choose a plan to enable more displays.</p>

      {plans.loading && !plans.data ? <Loading what="plans" /> : null}
      {plans.error ? <ErrorNote error={plans.error} /> : null}

      {plans.data ? (
        <>
          <div className="row" style={{ gap: 6 }}>
            <button
              className={interval === 'month' ? 'btn small primary' : 'btn small'}
              onClick={() => setInterval('month')}
            >
              Monthly
            </button>
            <button
              className={interval === 'year' ? 'btn small primary' : 'btn small'}
              onClick={() => setInterval('year')}
            >
              Yearly
            </button>
          </div>

          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            {plans.data.map((plan) => {
              const priced = interval === 'month' ? plan.hasMonthlyPrice : plan.hasYearlyPrice;
              return (
                <div key={plan.slug} className="card card-pad" style={{ minWidth: 220 }}>
                  <h3 style={{ marginTop: 0 }}>{plan.label}</h3>
                  <p className="muted small">
                    {plan.maxDisplays === null ? 'Unlimited displays' : `Up to ${plan.maxDisplays} displays`}
                  </p>
                  <button
                    className="btn primary"
                    disabled={!manage || !priced || subscribing !== null}
                    onClick={() => void subscribe(plan)}
                  >
                    {subscribing === plan.slug ? 'Redirecting…' : priced ? 'Subscribe' : 'Not available'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
